import { useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';

import AuthModal from '../../Shared/AuthModal';
import Footer from '../../Shared/NewFooter';
import Header from '../../Shared/NewHeader';
import { ContentWrapper, PageWrapper } from '../../styles';
import ListTitle from '../ListTitle';
import ListView from '../ListView';
import DetailForm from './DetailForm';

import { setInitialState } from '../../../store/hosted-venue';
import { getSearchResults } from '../../../store/search';
import { getUser } from '../../../store/user';
import { useHostVenueAddNewVenueTag } from '../../../tag-manager/host';
import { makeSearchUrl } from '../../helpers';
import { bodyStyles, colors, mobile, tablet } from '../../styles';
import { useSelectVenue } from '../hooks';

const SimplifiedContentWrapper = styled(ContentWrapper)`
  overflow: auto;

  & > div {
    overflow: visible;
  }
`;

const EditContentWrapper = styled(ContentWrapper)`
  flex-wrap: nowrap;
`;

const MobileWrapper = styled.div`
  ${bodyStyles}
  display: none;

  @media (max-width: ${mobile}) {
    ${({ showForm }) =>
      showForm &&
      css`
        display: block;
        box-sizing: border-box;
        padding: 24px;
        background-color: ${colors.whiteBackground};
        width: 100%;
        height: auto;
      `}
  }
`;

const NonMobileWrapper = styled(PageWrapper)`
  @media (max-width: ${mobile}) {
    ${({ showForm }) =>
      showForm &&
      css`
        display: none;
      `}
  }
`;

const ListWrapper = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  width: 100%;
  height: 100%;
  flex-basis: max-content;
  padding: 40px 44px 42px;
  box-sizing: border-box;

  @media (max-width: ${tablet}) {
    padding: 40px 24px 42px;
  }

  @media (max-width: ${mobile}) {
    padding: 30px 15px 42px;
    flex-basis: 100%;
    height: auto;
  }
`;

const DetailWrapper = styled.div`
  flex-shrink: 1;
  border-left: 1px solid ${colors.lightBorder};
  padding: 40px 44px 42px;
  min-width: 460px;

  &:first-child {
    border-left: none;
    flex-basis: 60%;

    @media (max-width: 1300px) {
      flex-basis: 70%;
    }
  }
  &:not(:first-child) {
    flex-grow: 1;
  }

  @media (max-width: ${tablet}) {
    padding: 40px 24px 42px;
  }

  @media (max-width: ${mobile}) {
    display: none;
  }
`;

function HostedVenues({ makeUrl = makeSearchUrl }) {
  const scrollRef = useRef(null);
  const searchResults = useSelector(getSearchResults);
  const user = useSelector(getUser);
  console.log('🚀 ~ HostedVenues ~ user:', user);
  const [showNewForm, setShowNewForm] = useState(false);
  const { selected, hasSelection, setSelectedVenue, clearSelectedVenue, pageResults, allResults, hasResults, totalPages, currentPage, setCurrentPage } =
    useSelectVenue({
      searchResults,
      makeUrl,
      scrollRef
    });

  const dispatch = useDispatch();

  // VS1.2: /host/venues/?new=1 (from /list-your-venue) opens the "add a
  // venue" form for a host who already has venues. Read once, on load.
  const { search } = useLocation();
  const history = useHistory();
  const [openNewOnLoad] = useState(() => new URLSearchParams(search).get('new') === '1');

  // If the page loaded with a venue already selected, get form ready
  useLayoutEffect(() => {
    if (openNewOnLoad) {
      dispatch(setInitialState()); // clean slate
      if (hasResults) {
        setShowNewForm(true);
      }
      history.replace('/host/venues/'); // drop ?new=1, so a reload does not open it again
    } else if (hasSelection) {
      dispatch(setInitialState(selected));
    }
  }, []);

  // Show simplified form if they don't have any venues yet
  if (!hasResults) {
    const detailView = <DetailForm selected={selected} />;
    return (
      <>
        <MobileWrapper showForm>{detailView}</MobileWrapper>
        <NonMobileWrapper showForm>
          <Header />
          <SimplifiedContentWrapper>
            <DetailWrapper>{detailView}</DetailWrapper>
          </SimplifiedContentWrapper>
          <Footer />
          <AuthModal />
        </NonMobileWrapper>
      </>
    );
  }

  // click to add a new venue
  const onAddVenueClick = !showNewForm
    ? () => {
        localStorage.setItem('hosted_add_venue_started', 'true');

        useHostVenueAddNewVenueTag({ label: 'add-new-venue', hostId: user.id, hostName: user.firstName + ' ' + user.lastName });

        dispatch(setInitialState()); // reset form to clean slate
        setShowNewForm(true); // open new form
        if (hasSelection) {
          // close edit form, if active
          clearSelectedVenue();
        }
      }
    : null;
  // click to edit a venue

  const setSelected = (id) => {
    const venueState = allResults.find((venue) => venue && venue.id === id);

    if (venueState) {
      dispatch(setInitialState(venueState)); // set form state to venue
      setShowNewForm(false); // close new form, if active
      setSelectedVenue(id); // open edit form
    }
  };
  // cancel new or edit venue
  const clearSelected = () => {
    if (hasSelection) {
      clearSelectedVenue();
    } else if (showNewForm) {
      setShowNewForm(false);
    }
  };

  const detailView = (hasSelection || showNewForm) && (
    <DetailForm isEditMode={hasSelection} id={hasSelection ? selected.id : null} clearSelected={clearSelected} />
  );

  return (
    <>
      {/* On phones the form replaces the list, for a new venue too. */}
      <MobileWrapper showForm={hasSelection || showNewForm}>{detailView}</MobileWrapper>
      <NonMobileWrapper showForm={hasSelection || showNewForm}>
        <Header />
        <EditContentWrapper>
          <ListWrapper ref={scrollRef}>
            <ListTitle defaultTitle="Hosted Venues" count={allResults.length} disableMapView onAddVenueClick={onAddVenueClick} />
            <ListView
              venueList={pageResults}
              selected={hasSelection ? selected : null}
              setSelectedVenue={setSelected}
              totalPages={totalPages}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              isHostView
            />
          </ListWrapper>
          <DetailWrapper>{detailView}</DetailWrapper>
        </EditContentWrapper>
        <Footer />
        <AuthModal />
      </NonMobileWrapper>
    </>
  );
}

export default HostedVenues;
